import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo, Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { BotonDeAnulacion } from '../componentes/ui/anulacion';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { conDosDecimales } from '../venta/PiezasDeVenta';

interface Proveedor {
  readonly id: string;
  readonly razonSocial: string;
}

interface Orden {
  readonly id: string;
  readonly numero: string;
  readonly estado: string;
  readonly fecha: string;
  readonly total: string;
  readonly proveedor: string;
  readonly proveedorId: string;
  readonly deposito: string;
}

interface LineaDeOrden {
  readonly id: string;
  readonly orden: number;
  readonly varianteId: string;
  readonly descripcion: string;
  readonly codigo: string;
  readonly unidad: string;
  readonly cantidadPedida: string;
  readonly cantidadRecibida: string;
  readonly precioUnitario: string;
}

interface Recepcion {
  readonly id: string;
  readonly numero: string;
  readonly fecha: string;
  readonly estado: string;
  readonly remitoDelProveedor: string | null;
  readonly proveedor: string;
  readonly deposito: string;
}

interface Factura {
  readonly id: string;
  readonly letra: string | null;
  readonly puntoDeVentaDelProveedor: string | null;
  readonly numeroDelProveedor: string;
  readonly fecha: string;
  readonly vencimiento: string | null;
  readonly total: string;
  readonly tieneDiferencias: boolean;
  readonly estado: string;
  readonly proveedor: string;
}

interface RenglonDelCotejo {
  readonly varianteId: string | null;
  readonly descripcion: string;
  readonly cantidadFacturada: string;
  readonly cantidadRecibida: string;
  readonly precioFacturado: string;
  readonly precioRecibido: string | null;
  readonly diferencia: string | null;
}

interface Cotejo {
  readonly lineas: readonly RenglonDelCotejo[];
  readonly sinFacturar: readonly RenglonDelCotejo[];
  readonly tieneDiferencias: boolean;
}

interface VarianteEncontrada {
  readonly varianteId: string;
  readonly codigo: string;
  readonly nombre: string;
}

interface LineaCargada {
  readonly clave: string;
  varianteId: string;
  etiqueta: string;
  cantidad: string;
  precioUnitario: string;
}

/** Cómo se nombra una variante al buscarla: "LATEX — Látex 20 litros". */
function etiquetaDeVariante(variante: VarianteEncontrada): string {
  return `${variante.codigo} — ${variante.nombre}`;
}

/** Lo recibido sobre lo pedido: "8 / 20 UN". */
function avanceDeLaLinea(linea: LineaDeOrden): string {
  return `${linea.cantidadRecibida} / ${linea.cantidadPedida} ${linea.unidad}`;
}

/** `facturado_de_mas` → `compras.difFacturadoDeMas`. */
function claveDeDiferencia(valor: string): string {
  const camello = valor.replace(/_(.)/g, (_, letra: string) => letra.toUpperCase());
  return `compras.dif${camello.charAt(0).toUpperCase()}${camello.slice(1)}`;
}

/** `parcial` → `compras.estadoParcial`. */
function claveDeEstado(valor: string): string {
  return `compras.estado${valor.charAt(0).toUpperCase()}${valor.slice(1)}`;
}

/**
 * Compras: los tres documentos que se cotejan.
 *
 * La pantalla está armada en el orden en que ocurren las cosas —orden, recepción, factura— y no
 * por tipo de dato. Es el mismo recorrido que hace la mercadería, y quien la usa está siguiendo
 * ese recorrido: pidió algo, llegó algo, le cobraron algo.
 *
 * El cotejo se muestra con los dos números al lado. Decir "hay una diferencia" sin decir cuál
 * obliga a ir a buscar el remito en papel, que es exactamente lo que el sistema tendría que
 * evitar.
 */
export function Compras(): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const puedeCrearOrden = usePermiso('COMPRAS.ORDEN.CREAR');
  const puedeEmitir = usePermiso('COMPRAS.ORDEN.EMITIR');
  const puedeRecibir = usePermiso('COMPRAS.RECEPCION.REGISTRAR');
  const puedeFacturar = usePermiso('COMPRAS.FACTURA.REGISTRAR');
  const puedeAnularRecepciones = usePermiso('COMPRAS.RECEPCION.ANULAR');
  const puedeAnularFacturas = usePermiso('COMPRAS.FACTURA.ANULAR');
  const puedeAplicarGasto = usePermiso('COMPRAS.GASTO.APLICAR');

  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const [ordenAbierta, setOrdenAbierta] = useState(false);
  const [proveedorId, setProveedorId] = useState('');
  const [lineas, setLineas] = useState<LineaCargada[]>([]);
  const [busqueda, setBusqueda] = useState('');

  const [recibiendo, setRecibiendo] = useState<Orden | null>(null);
  const [remito, setRemito] = useState('');
  const [recibidas, setRecibidas] = useState<Record<string, string>>({});

  const [gastoDe, setGastoDe] = useState<Recepcion | null>(null);
  const [concepto, setConcepto] = useState('');
  const [importeDelGasto, setImporteDelGasto] = useState('');
  const [criterio, setCriterio] = useState<'por_importe' | 'por_cantidad'>('por_importe');

  const [cotejoDe, setCotejoDe] = useState<Factura | null>(null);

  const proveedores = useQuery({
    queryKey: ['proveedores'],
    queryFn: () => pedir<Proveedor[]>('/terceros?soloProveedores=true'),
  });
  const ordenes = useQuery({
    queryKey: ['ordenes-de-compra'],
    queryFn: () => pedir<Orden[]>('/ordenes-de-compra'),
  });
  const recepciones = useQuery({
    queryKey: ['recepciones'],
    queryFn: () => pedir<Recepcion[]>('/recepciones'),
  });
  const facturas = useQuery({
    queryKey: ['facturas-de-proveedor'],
    queryFn: () => pedir<Factura[]>('/facturas-de-proveedor'),
  });

  const candidatos = useQuery({
    queryKey: ['articulos-para-comprar', busqueda],
    enabled: ordenAbierta && busqueda.trim().length >= 2,
    queryFn: () =>
      pedir<VarianteEncontrada[]>(`/articulos/buscar?texto=${encodeURIComponent(busqueda.trim())}`),
  });

  const detalle = useQuery({
    queryKey: ['orden', recibiendo?.id],
    enabled: recibiendo !== null,
    queryFn: () => pedir<{ lineas: LineaDeOrden[] }>(`/ordenes-de-compra/${recibiendo?.id ?? ''}`),
  });

  const cotejo = useQuery({
    queryKey: ['cotejo', cotejoDe?.id],
    enabled: cotejoDe !== null,
    queryFn: () => pedir<Cotejo>(`/facturas-de-proveedor/${cotejoDe?.id ?? ''}/cotejo`),
  });

  async function refrescar(): Promise<void> {
    await Promise.all([
      clienteDeConsultas.invalidateQueries({ queryKey: ['ordenes-de-compra'] }),
      clienteDeConsultas.invalidateQueries({ queryKey: ['recepciones'] }),
      clienteDeConsultas.invalidateQueries({ queryKey: ['existencias'] }),
    ]);
  }

  const crearOrden = useMutation({
    mutationFn: () =>
      pedir<{ numero: string }>('/ordenes-de-compra', {
        method: 'POST',
        cuerpo: {
          proveedorId,
          emitir: true,
          lineas: lineas.map((linea) => ({
            varianteId: linea.varianteId,
            cantidad: linea.cantidad.trim().replace(',', '.'),
            precioUnitario: linea.precioUnitario.trim().replace(',', '.'),
          })),
        },
      }),
    onSuccess: async () => {
      setOrdenAbierta(false);
      setLineas([]);
      setError(null);
      await refrescar();
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const registrarRecepcion = useMutation({
    mutationFn: () => {
      const aRecibir = (detalle.data?.lineas ?? [])
        .map((linea) => ({ linea, cantidad: recibidas[linea.id] ?? '' }))
        .filter(({ cantidad }) => cantidad.trim() !== '' && Number(cantidad) > 0);

      return pedir<{ numero: string; estadoDeLaOrden: string | null }>('/recepciones', {
        method: 'POST',
        cuerpo: {
          proveedorId: recibiendo?.proveedorId,
          ordenId: recibiendo?.id,
          ...(remito.trim() === '' ? {} : { remitoDelProveedor: remito.trim() }),
          lineas: aRecibir.map(({ linea, cantidad }) => ({
            varianteId: linea.varianteId,
            cantidad: cantidad.trim().replace(',', '.'),
            costoUnitario: linea.precioUnitario,
            lineaDeOrdenId: linea.id,
          })),
        },
      });
    },
    onSuccess: async (resultado) => {
      setRecibiendo(null);
      setRecibidas({});
      setRemito('');
      setError(null);
      setAviso(
        resultado.estadoDeLaOrden === null
          ? t('compras.recibidaSinOrden', { numero: resultado.numero })
          : t('compras.recibida', {
              numero: resultado.numero,
              estado: t(claveDeEstado(resultado.estadoDeLaOrden)),
            }),
      );
      await refrescar();
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const aplicarGasto = useMutation({
    mutationFn: () =>
      pedir<{ absorbidoPorElStock: string; noAbsorbido: string }>('/gastos-de-compra', {
        method: 'POST',
        cuerpo: {
          recepcionId: gastoDe?.id,
          concepto: concepto.trim(),
          importe: importeDelGasto.trim().replace(',', '.'),
          criterio,
        },
      }),
    onSuccess: async (resultado) => {
      setGastoDe(null);
      setConcepto('');
      setImporteDelGasto('');
      setError(null);
      setAviso(
        t('compras.gastoAplicado', {
          absorbido: conDosDecimales(resultado.absorbidoPorElStock),
          noAbsorbido: conDosDecimales(resultado.noAbsorbido),
        }),
      );
      await refrescar();
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const cambiarEstado = useMutation({
    mutationFn: ({ orden, accion }: { orden: Orden; accion: 'emision' | 'anulacion' }) =>
      pedir<void>(`/ordenes-de-compra/${orden.id}/${accion}`, { method: 'POST' }),
    onSuccess: async () => {
      await refrescar();
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const columnasDeOrden: Columna<Orden>[] = [
    { clave: 'numero', titulo: t('compras.orden'), contenido: (fila) => fila.numero },
    { clave: 'proveedor', titulo: t('compras.proveedor'), contenido: (fila) => fila.proveedor },
    { clave: 'fecha', titulo: t('compras.fecha'), contenido: (fila) => fila.fecha },
    {
      clave: 'estado',
      titulo: t('compras.estado'),
      contenido: (fila) => (
        <Insignia
          tono={
            fila.estado === 'cumplida' ? 'bien' : fila.estado === 'anulada' ? 'neutro' : 'atencion'
          }
        >
          {t(claveDeEstado(fila.estado))}
        </Insignia>
      ),
    },
    {
      clave: 'total',
      titulo: t('compras.total'),
      numerica: true,
      contenido: (fila) => <span className="numero">{conDosDecimales(fila.total)}</span>,
    },
    {
      clave: 'acciones',
      titulo: t('sucursales.acciones'),
      contenido: (fila) => (
        <div className="flex gap-1">
          {puedeEmitir && fila.estado === 'borrador' ? (
            <Boton
              tono="sutil"
              onClick={() => {
                cambiarEstado.mutate({ orden: fila, accion: 'emision' });
              }}
            >
              {t('compras.emitir')}
            </Boton>
          ) : null}
          {puedeRecibir && (fila.estado === 'emitida' || fila.estado === 'parcial') ? (
            <Boton
              tono="sutil"
              onClick={() => {
                setRecibiendo(fila);
                setRecibidas({});
                setError(null);
              }}
            >
              {t('compras.nuevaRecepcion')}
            </Boton>
          ) : null}
          {puedeEmitir && (fila.estado === 'borrador' || fila.estado === 'emitida') ? (
            <Boton
              tono="sutil"
              onClick={() => {
                cambiarEstado.mutate({ orden: fila, accion: 'anulacion' });
              }}
            >
              {t('compras.anular')}
            </Boton>
          ) : null}
        </div>
      ),
    },
  ];

  const columnasDeRecepcion: Columna<Recepcion>[] = [
    { clave: 'numero', titulo: t('compras.numero'), contenido: (fila) => fila.numero },
    { clave: 'fecha', titulo: t('compras.fecha'), contenido: (fila) => fila.fecha },
    { clave: 'proveedor', titulo: t('compras.proveedor'), contenido: (fila) => fila.proveedor },
    {
      clave: 'remito',
      titulo: t('compras.remito'),
      contenido: (fila) => <span className="numero">{fila.remitoDelProveedor ?? '—'}</span>,
    },
    { clave: 'deposito', titulo: t('compras.deposito'), contenido: (fila) => fila.deposito },
    {
      clave: 'gasto',
      titulo: t('compras.gasto'),
      contenido: (fila) =>
        puedeAplicarGasto ? (
          <Boton
            tono="sutil"
            onClick={() => {
              setGastoDe(fila);
              setError(null);
            }}
          >
            {t('compras.gasto')}
          </Boton>
        ) : null,
    },
    {
      clave: 'anular',
      titulo: t('cuentas.anular'),
      contenido: (fila) =>
        puedeAnularRecepciones && fila.estado === 'registrada' ? (
          <BotonDeAnulacion
            ruta={`/recepciones/${fila.id}/anulacion`}
            onAnulado={async () => {
              await clienteDeConsultas.invalidateQueries({ queryKey: ['recepciones'] });
              await clienteDeConsultas.invalidateQueries({ queryKey: ['existencias'] });
            }}
          />
        ) : null,
    },
  ];

  const columnasDeFactura: Columna<Factura>[] = [
    {
      clave: 'numero',
      titulo: t('compras.numero'),
      contenido: (fila) => (
        <span className="numero">
          {[fila.letra, fila.puntoDeVentaDelProveedor, fila.numeroDelProveedor]
            .filter((parte) => parte !== null && parte !== '')
            .join('-')}
        </span>
      ),
    },
    { clave: 'proveedor', titulo: t('compras.proveedor'), contenido: (fila) => fila.proveedor },
    { clave: 'fecha', titulo: t('compras.fecha'), contenido: (fila) => fila.fecha },
    {
      clave: 'vencimiento',
      titulo: t('compras.vencimientoDePago'),
      contenido: (fila) => fila.vencimiento ?? '—',
    },
    {
      clave: 'total',
      titulo: t('compras.total'),
      numerica: true,
      contenido: (fila) => <span className="numero">{conDosDecimales(fila.total)}</span>,
    },
    {
      clave: 'cotejo',
      titulo: t('compras.cotejo'),
      contenido: (fila) => (
        <div className="flex items-center gap-2">
          <Insignia tono={fila.tieneDiferencias ? 'atencion' : 'bien'}>
            {fila.tieneDiferencias ? t('compras.conDiferencias') : t('compras.sinDiferencias')}
          </Insignia>
          <Boton
            tono="sutil"
            onClick={() => {
              setCotejoDe(fila);
            }}
          >
            {t('compras.verCotejo')}
          </Boton>
          {puedeAnularFacturas && fila.estado === 'registrada' ? (
            <BotonDeAnulacion
              ruta={`/facturas-de-proveedor/${fila.id}/anulacion`}
              onAnulado={async () => {
                await clienteDeConsultas.invalidateQueries({ queryKey: ['facturas'] });
                await clienteDeConsultas.invalidateQueries({
                  queryKey: ['cuentas-de-proveedores'],
                });
              }}
            />
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <Encabezado
        titulo={t('compras.compras')}
        descripcion={t('compras.descripcionCompras')}
        acciones={
          puedeCrearOrden ? (
            <Boton
              tono="principal"
              onClick={() => {
                setOrdenAbierta(true);
                setProveedorId(proveedores.data?.[0]?.id ?? '');
                setError(null);
              }}
            >
              {t('compras.nuevaOrden')}
            </Boton>
          ) : undefined
        }
      />

      {aviso === null ? null : (
        <p role="status" className="mb-3 text-sm text-bien">
          {aviso}
        </p>
      )}
      {error === null ? null : (
        <p role="alert" className="mb-3 text-sm text-mal">
          {error}
        </p>
      )}

      <div className="flex flex-col gap-4">
        <Tarjeta titulo={t('compras.ordenes')}>
          {ordenes.isPending ? <Cargando /> : null}
          {ordenes.isError ? <Problema error={ordenes.error} /> : null}
          {ordenes.isSuccess ? (
            <Tabla
              columnas={columnasDeOrden}
              filas={ordenes.data}
              claveDeFila={(fila) => fila.id}
              vacio={<Vacio descripcion={t('compras.sinOrdenes')} />}
            />
          ) : null}
        </Tarjeta>

        <Tarjeta titulo={t('compras.recepciones')}>
          {recepciones.isSuccess ? (
            <Tabla
              columnas={columnasDeRecepcion}
              filas={recepciones.data}
              claveDeFila={(fila) => fila.id}
              vacio={<Vacio descripcion={t('compras.sinRecepciones')} />}
            />
          ) : null}
        </Tarjeta>

        <Tarjeta titulo={t('compras.facturas')}>
          {facturas.isSuccess ? (
            <Tabla
              columnas={columnasDeFactura}
              filas={facturas.data}
              claveDeFila={(fila) => fila.id}
              vacio={<Vacio descripcion={t('compras.sinFacturas')} />}
            />
          ) : null}
          {puedeFacturar ? (
            <p className="mt-3 text-xs text-texto-tenue">{t('compras.descripcionCompras')}</p>
          ) : null}
        </Tarjeta>
      </div>

      {/* --- Nueva orden ------------------------------------------------------------- */}
      <Dialogo
        abierto={ordenAbierta}
        titulo={t('compras.nuevaOrden')}
        onCerrar={() => {
          setOrdenAbierta(false);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={crearOrden.isPending}
            disabled={lineas.length === 0 || proveedorId === ''}
            onClick={() => {
              crearOrden.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Selector
            etiqueta={t('compras.proveedor')}
            obligatorio
            value={proveedorId}
            onChange={(evento) => {
              setProveedorId(evento.target.value);
            }}
          >
            {(proveedores.data ?? []).map((proveedor) => (
              <option key={proveedor.id} value={proveedor.id}>
                {proveedor.razonSocial}
              </option>
            ))}
          </Selector>

          <Campo
            etiqueta={t('compras.buscarArticulo')}
            type="search"
            value={busqueda}
            onChange={(evento) => {
              setBusqueda(evento.target.value);
            }}
          />

          {(candidatos.data ?? []).slice(0, 6).map((variante) => (
            <Boton
              key={variante.varianteId}
              tono="secundario"
              onClick={() => {
                setLineas((previas) => [
                  ...previas,
                  {
                    clave: `${variante.varianteId}-${String(previas.length)}`,
                    varianteId: variante.varianteId,
                    etiqueta: etiquetaDeVariante(variante),
                    cantidad: '1',
                    precioUnitario: '',
                  },
                ]);
                setBusqueda('');
              }}
            >
              {etiquetaDeVariante(variante)}
            </Boton>
          ))}

          {lineas.map((linea, indice) => (
            <div key={linea.clave} className="rounded-marco border border-borde p-2">
              <p className="mb-2 text-sm">{linea.etiqueta}</p>
              <div className="grid grid-cols-3 gap-2">
                <Campo
                  etiqueta={t('compras.cantidad')}
                  inputMode="decimal"
                  value={linea.cantidad}
                  onChange={(evento) => {
                    const valor = evento.target.value;
                    setLineas((previas) =>
                      previas.map((otra, otroIndice) =>
                        otroIndice === indice ? { ...otra, cantidad: valor } : otra,
                      ),
                    );
                  }}
                />
                <Campo
                  etiqueta={t('compras.precioUnitario')}
                  inputMode="decimal"
                  value={linea.precioUnitario}
                  onChange={(evento) => {
                    const valor = evento.target.value;
                    setLineas((previas) =>
                      previas.map((otra, otroIndice) =>
                        otroIndice === indice ? { ...otra, precioUnitario: valor } : otra,
                      ),
                    );
                  }}
                />
                <div className="flex items-end">
                  <Boton
                    tono="sutil"
                    onClick={() => {
                      setLineas((previas) =>
                        previas.filter((_, otroIndice) => otroIndice !== indice),
                      );
                    }}
                  >
                    {t('compras.quitar')}
                  </Boton>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Dialogo>

      {/* --- Recepción contra una orden ---------------------------------------------- */}
      <Dialogo
        abierto={recibiendo !== null}
        titulo={t('compras.nuevaRecepcion')}
        onCerrar={() => {
          setRecibiendo(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={registrarRecepcion.isPending}
            onClick={() => {
              registrarRecepcion.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('compras.remito')}
            maxLength={64}
            value={remito}
            onChange={(evento) => {
              setRemito(evento.target.value);
            }}
          />

          {/* Se muestra lo pedido y lo que ya llegó: recibir es decir cuánto de lo que falta
              vino esta vez, no volver a cargar el pedido entero. */}
          {(detalle.data?.lineas ?? []).map((linea) => (
            <div key={linea.id} className="rounded-marco border border-borde p-2">
              <p className="text-sm">{linea.descripcion}</p>
              <p className="numero mb-2 text-xs text-texto-tenue">{avanceDeLaLinea(linea)}</p>
              <Campo
                etiqueta={t('compras.cantidad')}
                inputMode="decimal"
                value={recibidas[linea.id] ?? ''}
                onChange={(evento) => {
                  const valor = evento.target.value;
                  setRecibidas((previas) => ({ ...previas, [linea.id]: valor }));
                }}
              />
            </div>
          ))}
        </div>
      </Dialogo>

      {/* --- Gasto prorrateado -------------------------------------------------------- */}
      <Dialogo
        abierto={gastoDe !== null}
        titulo={t('compras.gasto')}
        descripcion={t('compras.ayudaNoAbsorbido')}
        onCerrar={() => {
          setGastoDe(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={aplicarGasto.isPending}
            disabled={concepto.trim() === '' || importeDelGasto.trim() === ''}
            onClick={() => {
              aplicarGasto.mutate();
            }}
          >
            {t('acciones.confirmar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('compras.concepto')}
            obligatorio
            maxLength={200}
            value={concepto}
            onChange={(evento) => {
              setConcepto(evento.target.value);
            }}
          />
          <Campo
            etiqueta={t('compras.importe')}
            obligatorio
            inputMode="decimal"
            value={importeDelGasto}
            onChange={(evento) => {
              setImporteDelGasto(evento.target.value);
            }}
          />
          <Selector
            etiqueta={t('compras.criterio')}
            value={criterio}
            ayuda={t('compras.ayudaCriterio')}
            onChange={(evento) => {
              setCriterio(evento.target.value === 'por_cantidad' ? 'por_cantidad' : 'por_importe');
            }}
          >
            <option value="por_importe">{t('compras.porImporte')}</option>
            <option value="por_cantidad">{t('compras.porCantidad')}</option>
          </Selector>
        </div>
      </Dialogo>

      {/* --- Cotejo ------------------------------------------------------------------- */}
      <Dialogo
        abierto={cotejoDe !== null}
        titulo={t('compras.cotejo')}
        onCerrar={() => {
          setCotejoDe(null);
        }}
      >
        {cotejo.isPending ? <Cargando /> : null}
        {cotejo.isSuccess ? (
          <Tabla
            columnas={[
              {
                clave: 'descripcion',
                titulo: t('compras.articulo'),
                contenido: (fila: RenglonDelCotejo) => fila.descripcion,
              },
              {
                clave: 'facturado',
                titulo: t('compras.facturado'),
                numerica: true,
                contenido: (fila: RenglonDelCotejo) => (
                  <span className="numero">
                    {fila.cantidadFacturada}
                    <span className="block text-xs text-texto-tenue">
                      {conDosDecimales(fila.precioFacturado)}
                    </span>
                  </span>
                ),
              },
              {
                clave: 'recibido',
                titulo: t('compras.recibido'),
                numerica: true,
                contenido: (fila: RenglonDelCotejo) => (
                  <span className="numero">
                    {fila.cantidadRecibida}
                    <span className="block text-xs text-texto-tenue">
                      {fila.precioRecibido === null ? '—' : conDosDecimales(fila.precioRecibido)}
                    </span>
                  </span>
                ),
              },
              {
                clave: 'diferencia',
                titulo: t('compras.estado'),
                contenido: (fila: RenglonDelCotejo) =>
                  fila.diferencia === null ? (
                    <Insignia tono="bien">{t('compras.coincide')}</Insignia>
                  ) : (
                    <Insignia tono="atencion">{t(claveDeDiferencia(fila.diferencia))}</Insignia>
                  ),
              },
            ]}
            filas={[...cotejo.data.lineas, ...cotejo.data.sinFacturar]}
            claveDeFila={(fila) => `${fila.varianteId ?? 'sin'}-${fila.descripcion}`}
            vacio={<Vacio descripcion={t('compras.sinDatos')} />}
          />
        ) : null}
      </Dialogo>
    </>
  );
}
